import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, SITE_URL } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { HelpCircle, ChevronDown, ArrowRight, Loader2 } from "lucide-react";

export default function FaqHub() {
  const { data, isLoading } = useQuery({ queryKey: ["faq-hub"], queryFn: async () => (await api.get("/faqs/hub")).data });
  const [open, setOpen] = useState({});
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const cats = data?.categories || [];
  const allFaqs = cats.flatMap((c) => c.faqs).slice(0, 50);
  const jsonLd = allFaqs.length ? [{
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: allFaqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  }] : [];

  return (
    <>
      <Seo title="HVAC FAQ Hub — Brevard County Answers | Aquila Air & Heating"
        description="Straight answers to the most common HVAC questions for Brevard County, FL homeowners — AC repair, humidity, costs, maintenance, heat pumps, Lifetime Warranty and more."
        path="/faq/" jsonLd={jsonLd} />

      <section className="relative bg-navy text-white dot-grid" data-testid="faq-hub-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "HVAC FAQ Hub" }]} /></div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><HelpCircle className="h-4 w-4" /> Answers for Space Coast Homeowners</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">HVAC FAQ Hub</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">Clear, local answers to the HVAC questions Brevard County homeowners ask most — grouped by topic, with links to learn more.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              <nav className="flex flex-wrap gap-2 mb-8" data-testid="faq-hub-nav">
                {cats.map((c) => (
                  <a key={c.key} href={`#${c.key}`} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-navy hover:bg-cool/10 border border-slate-200">{c.label} <span className="text-slate-400">({c.faqs.length})</span></a>
                ))}
              </nav>
              {cats.map((c) => (
                <div key={c.key} id={c.key} className="mb-10 scroll-mt-24" data-testid={`faq-cat-${c.key}`}>
                  <h2 className="text-2xl font-bold text-navy mb-4">{c.label}</h2>
                  <div className="space-y-2">
                    {c.faqs.map((f, i) => {
                      const id = `${c.key}-${i}`;
                      return (
                        <div key={id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`faq-item-${id}`}>
                          <button onClick={() => toggle(id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`faq-q-${id}`}>
                            <span className="font-semibold text-navy text-sm">{f.q}</span>
                            <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open[id] ? "rotate-180" : ""}`} />
                          </button>
                          {open[id] && (
                            <div className="px-4 pb-4 -mt-1">
                              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{f.a}</p>
                              {f.source_url && (
                                <Link to={f.source_url} className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-launch hover:underline" data-testid={`faq-source-${id}`}>
                                  More about {f.source_label} <ArrowRight className="h-3 w-3" />
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>
      <CtaBlock heading="Still have questions? We're Ready for Liftoff." />
    </>
  );
}
